/**
 * Pure presentation-layer helpers shared by the lifted feature
 * components. No platform APIs, no I/O — safe under any browser-class
 * environment plus jsdom / Node.
 */

/**
 * Format a timestamp as a human-readable relative string.
 *
 *   - <60s         → "just now"
 *   - <60m         → `${m}m ago`
 *   - <24h         → `${h}h ago`
 *   - 1 day ago    → "Yesterday"
 *   - older        → locale-formatted "Mon DD" via `Date.toLocaleDateString`
 *
 * Locale defaults to the user's browser locale via `undefined`. Calls
 * `Date.now()` internally — pass a fixed clock by computing the
 * timestamp delta yourself if you need deterministic output (the
 * existing tests in extension's primitives.test.tsx pin the relative
 * tolerance, not the wall-clock value).
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = Math.max(0, now - timestamp);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "Yesterday";

  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Convert a base64-encoded image payload to a `data:image/<mime>;base64,...`
 * URL by sniffing the magic-byte prefix of the encoded string.
 *
 * Android mirrors notification images and app icons as raw base64
 * without a mime hint in the wire format — signing the mime in the
 * payload is a pending schema change. Until then, the magic-byte
 * prefixes of the base64 encoding are a reliable sniff:
 *   - PNG  → `iVBOR`         (0x89 P N G)
 *   - WebP → `UklGR`         (R I F F ... W E B P)
 *   - GIF  → `R0lGO`         (G I F 8 _)
 *   - JPEG → `/9j/`          (0xFF 0xD8 0xFF) — fall-through default
 *
 * JPEG is the fall-through. Any unrecognized prefix also falls to JPEG,
 * matching the legacy assumption — harmless for valid content.
 */
export function base64ImageDataUrl(base64: string): string {
  let mime: "jpeg" | "png" | "webp" | "gif" = "jpeg";
  if (base64.startsWith("iVBOR")) mime = "png";
  else if (base64.startsWith("UklGR")) mime = "webp";
  else if (base64.startsWith("R0lGO")) mime = "gif";
  return `data:image/${mime};base64,${base64}`;
}
